/**
 * 表セクション配下のノード(行セクション・セル質問)を生成する純粋なビルダー群。
 * 固定行の更新(tableFixedRow.ts)と列の更新(tableColumn.ts)で共有する。
 * createNode.ts の createNewTemplateNode と同等だが、Redux 依存の循環を避けるためここに置く。
 */

import {
  ResponseDatetimeSubTypeEnum,
  ResponseEmployeeSubTypeEnum,
  ResponseTextSubTypeEnum,
  ResponseTypeEnum,
  SectionTypeEnum,
  TemplateNodeSchema,
  TemplateNodeTypeEnum,
  TemplateQuestion,
} from '@ulysses-inc/harami_api_client'
import { UUID } from 'src/exShared/util/uuid'
import { DEFAULT_DEVIATE_COMMENT } from 'src/features/templateEdit/common/types'
import type { ColumnInputType } from './columnType'

/** 新規 TemplateNode の共通部分(id / uuid / type / 空 nodes)を作る。 */
const createTemplateNodeShell = (
  id: number,
  type: TemplateNodeTypeEnum,
): TemplateNodeSchema => ({
  id,
  uuid: UUID(),
  type,
  nodes: [],
})

/**
 * 列入力種別から、新規列(または分岐質問)の初期質問定義を得る。
 *
 * 記録アプリ(harami_report)側の変換は responseTexts / responseDatetimes の
 * subType を必須としているため、初期値を必ず付与すること。
 *
 * NOTE: responseType ごとの初期 response 系フィールドの正は、通常質問の
 * 回答形式変更(question/QuestionBody.tsx の onChangeQuestionType)にある。
 * 現状はコンポーネント内の switch で再利用できないため、ここはその複製。
 * 内容は QuestionBody 側と一致させること。
 */
export const QUESTION_FROM_RESPONSE_TYPE = {
  [ResponseTypeEnum.NUMBER]: {
    responseType: ResponseTypeEnum.NUMBER,
    responseNumbers: [],
  },
  [ResponseTypeEnum.DATETIME]: {
    responseType: ResponseTypeEnum.DATETIME,
    responseDatetimes: [{ subType: ResponseDatetimeSubTypeEnum.DATETIME }],
  },
  [ResponseTypeEnum.MULTIPLE_CHOICE]: {
    responseType: ResponseTypeEnum.MULTIPLE_CHOICE,
    responseMultipleChoices: [],
    responseMultipleChoiceSets: [],
  },
  [ResponseTypeEnum.RESPONSE_SET]: {
    responseType: ResponseTypeEnum.RESPONSE_SET,
    responseMultipleChoices: [],
    responseMultipleChoiceSets: [],
  },
  [ResponseTypeEnum.SIGNATURE]: { responseType: ResponseTypeEnum.SIGNATURE },
  [ResponseTypeEnum.TEXT]: {
    responseType: ResponseTypeEnum.TEXT,
    responseTexts: [{ subType: ResponseTextSubTypeEnum.SINGLE_LINE }],
  },
  [ResponseTypeEnum.RESULT_IMAGE]: {
    responseType: ResponseTypeEnum.RESULT_IMAGE,
  },
  [ResponseTypeEnum.EMPLOYEE]: {
    responseType: ResponseTypeEnum.EMPLOYEE,
    responseEmployees: [
      { subType: ResponseEmployeeSubTypeEnum.NAME, isMultiple: 0 },
    ],
  },
  [ResponseTypeEnum.FORMULA]: {
    responseType: ResponseTypeEnum.FORMULA,
    responseFormulas: [],
  },
  [ResponseTypeEnum.TIME_MEASUREMENT]: {
    responseType: ResponseTypeEnum.TIME_MEASUREMENT,
    responseTimeMeasurements: [],
  },
} satisfies Record<ColumnInputType, TemplateQuestion>

/** 列またはセル用の質問ノード。ひな形編集の新規質問と同様のデフォルト値を付与する。 */
export const buildNewQuestionNode = (
  id: number,
  question: TemplateQuestion,
): TemplateNodeSchema => ({
  ...createTemplateNodeShell(id, TemplateNodeTypeEnum.Question),
  question: {
    ...question,
    name: question.name ?? '',
    isShowInformationImages: 0,
    informationImages: [],
    hints: [],
    deviateComment: DEFAULT_DEVIATE_COMMENT,
  },
})

/** 表セクション配下の1行。固定行は `isRepeat = 0`、テンプレート行は `isRepeat = 1`。 */
export const buildNewRowSectionNode = (
  id: number,
  isRepeat: 0 | 1,
): TemplateNodeSchema => ({
  ...createTemplateNodeShell(id, TemplateNodeTypeEnum.Section),
  section: { name: '', sectionType: SectionTypeEnum.TableRow, isRepeat },
})
